import { SkyDialog, SkyInput, SkyText } from "@skypointcloud/skypoint-ui";
import { useMemo, useState } from "react";
import { addDaysToDate, isRealDate, startOfLocalDate } from "@shared/cron/cronUtils";
import { MAX_PAUSE_DAYS, type ScheduleSummary } from "@shared/types/schedules";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { zoneLabel } from "@/lib/time/displayTime";
import { formatPauseEnd, formatSkipDate, todayInZone } from "./scheduleForm";
import { usePauseSchedule } from "./useSchedulePause";
import { useNotice } from "@/lib/feedback/NoticeProvider";

/**
 * "Pause…" from a schedule's row menu (Session 10, P9).
 *
 * The user picks the DAY the schedule starts again; nothing fires before the start of that day
 * in the reader's display zone, and an occurrence exactly then fires (the server's rule). The
 * pause end is an instant the reader chooses, so it is chosen, confirmed and then shown in their
 * display zone, named, on every screen (`formatPauseEnd`, #1078 review: the dialog worked in the
 * schedule's zone while the list read the same end in the display zone, a day apart). A pause
 * ends by itself, and Resume on the same menu ends it sooner. The earliest day is tomorrow and
 * the latest is a year ahead (the server refuses more than `MAX_PAUSE_DAYS`; one day of margin
 * keeps a far zone's midnight inside it).
 *
 * A failure stays inside the dialog, which stays open (ux-s10.md §3: errors inline where the
 * control remains); success closes it and confirms.
 *
 * A date field, not `SkyDatePicker`: the 2.4.0 picker fails axe `aria-allowed-attr` (critical) on
 * its popover trigger, as `ScheduleSkipDates.tsx` explains.
 */

export interface PauseScheduleDialogProps {
  schedule: ScheduleSummary;
  onClose: () => void;
}

export function PauseScheduleDialog({ schedule, onClose }: PauseScheduleDialogProps) {
  const notice = useNotice();
  const pause = usePauseSchedule();
  const inline = useInlineError();
  const [value, setValue] = useState("");
  const zone = useDisplayTz();

  // Tomorrow in the DISPLAY zone: the browser's tomorrow can already be today there (a saved
  // zone other than this device's), and the server refuses a pause that ends in the past.
  const { tomorrow, latest } = useMemo(() => {
    const today = todayInZone(zone);
    return { tomorrow: addDaysToDate(today, 1), latest: addDaysToDate(today, MAX_PAUSE_DAYS - 1) };
  }, [zone]);
  const real = isRealDate(value) ? value : null;
  const outOfRange = real !== null && (real < tomorrow || real > latest);
  const day = real !== null && !outOfRange ? real : null;

  const submit = async (): Promise<void> => {
    if (!day) return;
    inline.clear();
    try {
      const until = startOfLocalDate(day, zone).toISOString();
      const saved = await pause.mutateAsync({ id: schedule.id, until });
      onClose();
      // After the close, so the notice is posted outside the modal's hidden window.
      notice.success(
        `${saved.name} is paused until ${formatPauseEnd(saved.pausedUntil ?? until, zone)}.`,
      );
    } catch (error) {
      inline.show(error, "The schedule could not be paused.");
    }
  };

  return (
    <SkyDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      onBeforeClose={(reason) => reason !== "primaryAction"}
      size="medium"
      testId="pause-schedule-dialog"
      title={`Pause ${schedule.name}`}
      description="No import starts while a schedule is paused. It starts again by itself on the day you choose."
      primaryAction={{
        label: "Pause schedule",
        onClick: () => void submit(),
        loading: pause.isPending,
        disabled: !day || pause.isPending,
      }}
      secondaryAction={{ label: "Cancel", onClick: onClose }}
    >
      <div className="flex flex-col gap-3">
        <SkyInput
          type="date"
          label="Start again on"
          required
          value={value}
          onChange={(event) => setValue(event.target.value)}
          hint={`At 12:00 AM, ${zoneLabel(zone)}. A run already in progress finishes.`}
          validationState={outOfRange ? "error" : "none"}
          validationMessage={
            outOfRange
              ? `Choose a day from ${formatSkipDate(tomorrow)} to ${formatSkipDate(latest)}.`
              : undefined
          }
          testId="pause-schedule-until"
        />
        {day ? (
          <SkyText size={200} testId="pause-schedule-summary">
            {`No import starts until ${formatSkipDate(day)}, 12:00 AM ${zoneLabel(zone)}.`}
          </SkyText>
        ) : null}
        {/* No title on a retryable failure: the bar's own "Try again" title shows (P13a). */}
        <InlineErrorBar
          error={inline.error}
          title={inline.error?.retryable ? undefined : "We could not pause this schedule"}
          testId="pause-schedule-error"
        />
      </div>
    </SkyDialog>
  );
}
